import React, { useState } from 'react';
import type { User, CurrentUser, University, UserFormData } from '../../types';
import { checkEmailExists } from '../../services/userService';
import Modal from '../ui/Modal';

interface UserFormProps {
  user?: User | null;
  universities: University[];
  currentUser: CurrentUser;
  onSave: (data: UserFormData) => Promise<void>;
  onClose: () => void;
}

const UserForm: React.FC<UserFormProps> = ({ user, universities, currentUser, onSave, onClose }) => {
  const isSuperAdmin = currentUser.role === 'super_admin';

  const [formData, setFormData] = useState<UserFormData>({
    name: user?.name || '',
    email: user?.email || '',
    role: user?.role === 'super_admin' ? 'admin' : (user?.role || (isSuperAdmin ? 'admin' : 'user')) as 'admin' | 'user',
    status: (user?.status || 'active') as 'active' | 'inactive',
    universityId: user?.universityId || universities[0]?.id || '',
    password: isSuperAdmin && !user ? '' : undefined,
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (saving) return;

    setError('');

    if (!formData.name || !formData.email || !formData.universityId) {
      setError('Please fill in all required fields.');
      return;
    }

    if (!user && isSuperAdmin && (!formData.password || formData.password.length < 6)) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    setSaving(true);
    try {
      if (!user) {
        const emailExists = await checkEmailExists(formData.email);
        if (emailExists) {
          setError(`Email address "${formData.email}" is already registered.`);
          return;
        }
      }

      await onSave(formData);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save user.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={user ? 'Edit User' : `Add New ${isSuperAdmin ? 'Administrator' : 'User'}`} onClose={onClose}>
      <div className="p-6 space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Enter full name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Enter email address"
          />
        </div>

        {!user && isSuperAdmin && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={formData.password || ''}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter password (min 6 characters)"
            />
            <p className="text-xs text-gray-500 mt-1">Minimum 6 characters. This will be the user's definitive password.</p>
          </div>
        )}

        {isSuperAdmin && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">University</label>
            <select
              value={formData.universityId}
              onChange={(e) => setFormData({ ...formData, universityId: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Select University</option>
              {universities.map(uni => (
                <option key={uni.id} value={uni.id}>{uni.name}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div className="flex space-x-3 pt-4">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : user ? 'Update' : 'Add'} {isSuperAdmin ? 'Admin' : 'User'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default UserForm;
